import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { projectService, Project, CreateProjectPayload } from '../services/project.service';

export const useProjects = (filters?: {
  project_status?: string;
  project_manager?: string;
  customer_id?: string;
  is_active?: boolean;
  search?: string;
}) => {
  return useQuery({
    queryKey: ['projects', filters],
    queryFn: () => projectService.getProjects(filters)
  });
};

export const useProject = (id: string) => {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () => projectService.getProjectById(id),
    enabled: !!id
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (data: Partial<Project>) =>
      projectService.createProject(data as CreateProjectPayload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      message.success('Project created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create project');
    }
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Project> }) =>
      projectService.updateProject(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.id] });
      message.success('Project updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update project');
    }
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => projectService.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      message.success('Project deleted successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete project');
    }
  });
};

// New hooks for project selection and quick create

export const useProjectsForSelection = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['projects', 'selection'],
    queryFn: () => projectService.getProjectsForSelection(),
    enabled: options?.enabled !== false,
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  });
};

export const useCreateQuickProject = () => {
  const queryClient = useQueryClient();
  // Don't show automatic messages here - let the modal handle it

  return useMutation({
    mutationFn: (data: {
      project_name: string;
      customer_name: string;
      customer_id?: string;
      start_date: string;
      department_id?: string;
    }) => projectService.quickCreateProject(data),
    onSuccess: () => {
      // Invalidate both selection and regular project queries
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    }
  });
};
